import { lazy } from "react";
import SplashScreen from "../components/base/SplashScreen";
import type { RouteObject } from "react-router-dom";
import { Navigate } from "react-router-dom";
import RoleProtectedRoute from "./RoleProtectedRoute";
import AccessProtectedRoute from "./AccessProtectedRoute";
import { findAppRouteAccess } from "../config/access/routes";
import { TOP_ROLE_IDS } from "../hooks/useIsReseller";
import FlagsOperatorRoute from "./FlagsOperatorRoute";
import AdminIndexRedirect from "./AdminIndexRedirect";

// Layouts (Home, Lite, Admin)
const AdminLayout = lazy(() => import("../Layouts/admin/AdminLayout"));
const LiteLayout = lazy(() => import("../Layouts/lite/LiteLayout"));
const ReportLayout = lazy(() => import("../Layouts/reports/ReportLayout"));
const FacturacionLayout = lazy(() => import("../Layouts/facturacion/FacturacionLayout"));
const CobranzaLayout = lazy(() => import("../Layouts/cobranza/CobranzaLayout"));
// Cobranza (CC-CONSOLE #1264): la abre quien opera Cobranza —admin de plataforma,
// owner o el rol finance—, con la misma regla que la API (assertCobranzaOperator).
import CobranzaOperatorRoute from "./CobranzaOperatorRoute";
const LicenciasLayout = lazy(() => import("../Layouts/licencias/LicenciasLayout"));
const FeatureFlagsLayout = lazy(() => import("../Layouts/feature-flags/FeatureFlagsLayout"));
const ComportamientoLayout = lazy(() => import("../Layouts/comportamiento/ComportamientoLayout"));
const AnaliticasIndex = lazy(() => import("../Layouts/comportamiento/AnaliticasIndex"));
const AnaliticasDashboardPage = lazy(() => import("../Layouts/comportamiento/AnaliticasDashboardPage"));
const AnaliticasUsuariosPage = lazy(() => import("../Layouts/comportamiento/AnaliticasUsuariosPage"));
// Feature Flags: la misma app que CC-CONSOLE (Fase 2 de la sincronización). La
// sección ya no vive en reseller-admin.
const FeatureFlagsSection = lazy(() => import("../Layouts/feature-flags/FeatureFlagsSection"));
const ResellerAdminLayout = lazy(() => import("../Layouts/reseller-admin/ResellerAdminLayout"));
const PapeleraLayout = lazy(() => import("../Layouts/papelera/PapeleraLayout"));
const PapeleraSection = lazy(() => import("../Layouts/papelera/PapeleraSection"));
const PapeleraIndexRedirect = lazy(() => import("../Layouts/papelera/PapeleraIndexRedirect"));
const EntregaServicioLayout = lazy(() => import("../Layouts/entrega-servicio/EntregaServicioLayout"));
const DashboardsLayout = lazy(() => import("../Layouts/dashboards/DashboardsLayout"));
const SupportLayout = lazy(() => import("../Layouts/support/SupportLayout"));
const SupportChatPage = lazy(() => import("../pages/support/SupportChatPage"));
import { crmRoute } from "../Layouts/crm/routes";

// Nuvex Access (front-end scaffold)
import { nuvexAccessRoute } from "../Layouts/nuvex-access/routes";

// Gestión de Staff — Actividades (front-end scaffold, issue #60)
import { staffRoute } from "../Layouts/staff/routes";
import { clientViewRoutes } from "../Layouts/client-view/routes";

// Gestión de Staff y Finanzas internas (portadas de CC-CONSOLE)
import { teamRoute } from "../Layouts/team/routes";
import { payrollRoute } from "../Layouts/payroll/routes";
import { workHoursRoute } from "../Layouts/work-hours/routes";
import { expensesRoute } from "../Layouts/expenses/routes";
import { financeSettingsRoute } from "../Layouts/finance-settings/routes";
import { gateChildPathsByFeature, gateRouteByFeature } from "./platformFeatureRoutes";
import { isPlatformFeatureEnabled } from "../config/platformFeatures";

// Mantenimiento a Vehículos (mini-app)
import { mantenimientoRoute } from "../Layouts/mantenimiento/routes";
// Documentación — expediente documental de embarques (CC-CONSOLE #1632)
import { documentacionRoute } from "../Layouts/documentacion/routes";
import { finanzasOperativasRoute } from "../Layouts/finanzas-operativas/routes";

import { monitoreoIaRoute } from "../Layouts/monitoreo-ia/routes";
// Radar de Correo: lectura de correo con IA para logística (operaciones, alertas, Snapshot).
import { radarCorreoRoute } from "../Layouts/radar-correo/routes";
import { chatIaRoute } from "../Layouts/chat-ia/routes";

// Cargo Market (front-end scaffold) — marketplace + cotizador de carga
import { cargoMarketRoute } from "../Layouts/cargo-market/routes";

// Bases y Propiedades — Amenidades & Reservas (front-end scaffold)
import { basesRoute, amenidadesRoute, reservasRoute, rondinesRoute } from "../Layouts/bases-propiedades/routes";
// Activos (épica común CC-CONSOLE ↔ WAYAK, #1638): app idéntica en los dos forks.
import { activosRoute } from "../Layouts/activos/routes";
const PublicSpacesPage = lazy(() => import("../pages/public/spaces/PublicSpacesPage"));

// Service Desk (Mesa de Servicio) — agent workspace
import { serviceDeskRoute } from "../Layouts/service-desk/routes";
import { helpRoute, legacyHelpRoute } from "../Layouts/help/routes";

// Envíos (Gestión de Envíos) — shipment management
import { enviosRoute } from "../Layouts/envios/routes";
// B2C → Movik: panel interno de operación (sólo administradores de plataforma).
import { movikLegacyRoutes, movikRoute } from "../Layouts/b2c-movik/routes";

// Service Desk — public client portal + authenticated "my tickets"
const SoporteLayout = lazy(() => import("../pages/public/soporte/SoporteLayout"));
const SoporteNuevo = lazy(() => import("../pages/public/soporte/SoporteNuevo"));
const SoporteSeguimiento = lazy(() => import("../pages/public/soporte/SoporteSeguimiento"));
const MisTicketsPage = lazy(() => import("../pages/soporte/MisTicketsPage"));
// Vincular dispositivo — autoservicio del funnel v2 (CC-CONSOLE#1523)
const VincularDispositivoView = lazy(() => import("../pages/devices/link/VincularDispositivoView"));

const IntegradoresLayout = lazy(() => import("../Layouts/integradores/IntegradoresLayout"));
const TruckYardLayout = lazy(() => import("../Layouts/patio-maniobras/TruckYardLayout"));
const InspectionsLayout = lazy(() => import("../Layouts/inspeccion/InspectionsLayout"));
const RespaldosLayout = lazy(() => import("../Layouts/respaldos/RespaldosLayout"));
const RespaldosList = lazy(() => import("../pages/respaldos/list/RespaldosList"));
const JobDetail = lazy(() => import("../pages/respaldos/detail/JobDetail"));
const InspectionUnitsSectionWrapper = lazy(() => import("../pages/inspecciones/InspectionUnitsSectionWrapper"));
const InspectionHistorySection = lazy(() => import("../pages/inspecciones/inspectionHistory/inspectionHistorySection"));
const YardsSection = lazy(() => import("../pages/patios-maniobras/yardsSection/page"));
const PersonalSection = lazy(() => import("../pages/patios-maniobras/personalSection/page"));
const AccessSection = lazy(() => import("../pages/patios-maniobras/accessSection/page"));
const AccessControlSection = lazy(() => import("../pages/patios-maniobras/accessControlSection/page"));
const AccessRecordsSection = lazy(() => import("../pages/patios-maniobras/accessRecordsSection/page"));
const RelaySection = lazy(() => import("../pages/patios-maniobras/relaySection/page"));
const VehiclesSection = lazy(() => import("../pages/patios-maniobras/vehiclesSection/page"));
const TruckYardGroupsSection = lazy(() => import("../pages/patios-maniobras/groupsSection/page"));
const AccessCardsSection = lazy(() => import("../pages/patios-maniobras/accessCardsSection/page"));

// POS Layouts
const POSCashierLayout = lazy(() => import("../Layouts/pos/cashier/POSCashierLayout"));
const POSProductsLayout = lazy(() => import("../Layouts/pos/products/POSProductsLayout"));
const POSInventoryLayout = lazy(() => import("../Layouts/pos/inventory/POSInventoryLayout"));
const POSCouponsLayout = lazy(() => import("../Layouts/pos/coupons/POSCouponsLayout"));
const POSOrdersLayout = lazy(() => import("../Layouts/pos/orders/POSOrdersLayout"));
const POSEcommerceLayout = lazy(() => import("../Layouts/pos/ecommerce/POSEcommerceLayout"));

// POS Pages
const TerminalSection = lazy(() => import("../pages/pos/cashier/TerminalSection"));
const QuickSaleSection = lazy(() => import("../pages/pos/cashier/QuickSaleSection"));
const CartSection = lazy(() => import("../pages/pos/cashier/CartSection"));
const CheckoutSection = lazy(() => import("../pages/pos/cashier/CheckoutSection"));
const ReceiptsSection = lazy(() => import("../pages/pos/cashier/ReceiptsSection"));
const RegisterSection = lazy(() => import("../pages/pos/cashier/RegisterSection"));
const ProductListSection = lazy(() => import("../pages/pos/products/ProductListSection"));
const CreateProductSection = lazy(() => import("../pages/pos/products/CreateProductSection"));
const CategoriesSection = lazy(() => import("../pages/pos/products/CategoriesSection"));
const VariantsSection = lazy(() => import("../pages/pos/products/VariantsSection"));
const PricingSection = lazy(() => import("../pages/pos/products/PricingSection"));
const ImportSection = lazy(() => import("../pages/pos/products/ImportSection"));
const ExportSection = lazy(() => import("../pages/pos/products/ExportSection"));
const StockSection = lazy(() => import("../pages/pos/inventory/StockSection"));
const LowStockSection = lazy(() => import("../pages/pos/inventory/LowStockSection"));
const AdjustmentsSection = lazy(() => import("../pages/pos/inventory/AdjustmentsSection"));
const TransfersSection = lazy(() => import("../pages/pos/inventory/TransfersSection"));
const MovementsSection = lazy(() => import("../pages/pos/inventory/MovementsSection"));
const LocationsSection = lazy(() => import("../pages/pos/inventory/LocationsSection"));
const SuppliersSection = lazy(() => import("../pages/pos/inventory/SuppliersSection"));
const CouponListSection = lazy(() => import("../pages/pos/coupons/CouponListSection"));
const CreateCouponSection = lazy(() => import("../pages/pos/coupons/CreateCouponSection"));
const PromotionsSection = lazy(() => import("../pages/pos/coupons/PromotionsSection"));
const DiscountsSection = lazy(() => import("../pages/pos/coupons/DiscountsSection"));
const AnalyticsSection = lazy(() => import("../pages/pos/coupons/AnalyticsSection"));
const UsageHistorySection = lazy(() => import("../pages/pos/coupons/UsageHistorySection"));
const SalesSection = lazy(() => import("../pages/pos/orders/SalesSection"));
const POSOrdersSection = lazy(() => import("../pages/pos/orders/POSOrdersSection"));
const OnlineOrdersSection = lazy(() => import("../pages/pos/orders/OnlineOrdersSection"));
const CounterPickupSection = lazy(() => import("../pages/pos/orders/CounterPickupSection"));
const RefundsSection = lazy(() => import("../pages/pos/orders/RefundsSection"));
const ReturnsSection = lazy(() => import("../pages/pos/orders/ReturnsSection"));
const OrdersReceiptsSection = lazy(() => import("../pages/pos/orders/ReceiptsSection"));
const ReportsSection = lazy(() => import("../pages/pos/orders/ReportsSection"));
const EcommerceDashboard = lazy(() => import("../pages/pos/ecommerce/EcommerceDashboard"));
const StorefrontSettings = lazy(() => import("../pages/pos/ecommerce/StorefrontSettings"));
const StoreThemePage = lazy(() => import("../pages/pos/ecommerce/StoreThemePage"));
const StorePagesPage = lazy(() => import("../pages/pos/ecommerce/StorePagesPage"));
const StoreNavigationPage = lazy(() => import("../pages/pos/ecommerce/StoreNavigationPage"));
const ShippingPage = lazy(() => import("../pages/pos/ecommerce/ShippingPage"));
const PaymentsPage = lazy(() => import("../pages/pos/ecommerce/PaymentsPage"));
const StoreSettingsPage = lazy(() => import("../pages/pos/ecommerce/StoreSettingsPage"));

// Passenger Transport (Gestión de Pasajeros) — epic #10
const PassengersLayout = lazy(() => import("../Layouts/passengers/PassengersLayout"));
const PaxHomeSection = lazy(() => import("../pages/passengers/HomeSection"));
const PaxRidesSection = lazy(() => import("../pages/passengers/RidesSection"));
const PaxRideManageSection = lazy(() => import("../pages/passengers/RideManageSection"));
const PaxStaffPosSection = lazy(() => import("../pages/passengers/StaffPosSection"));
const PaxPostTicketSection = lazy(() => import("../pages/passengers/PostTicketSection"));
const PaxUsersSection = lazy(() => import("../pages/passengers/UsersSection"));
const PaxStaffScanSection = lazy(() => import("../pages/passengers/StaffScanSection"));
const PaxSettingsSection = lazy(() => import("../pages/passengers/SettingsSection"));
const PaxStorefrontSection = lazy(() => import("../pages/passengers/StorefrontSection"));

const StorefrontLayout = lazy(() => import("../Layouts/public/StorefrontLayout"));
const StoreHome = lazy(() => import("../pages/public/store/StoreHome"));
const StoreCatalog = lazy(() => import("../pages/public/store/StoreCatalog"));
const StoreProduct = lazy(() => import("../pages/public/store/StoreProduct"));
const StoreCart = lazy(() => import("../pages/public/store/StoreCart"));
const StoreQuote = lazy(() => import("../pages/public/store/StoreQuote"));
const StoreCheckout = lazy(() => import("../pages/public/store/StoreCheckout"));
const StoreOrderSuccess = lazy(() => import("../pages/public/store/StoreOrderSuccess"));
const StorePickup = lazy(() => import("../pages/public/store/StorePickup"));
const PublicPickupPage = lazy(() => import("../pages/public/store/PublicPickupPage"));
const StorePageView = lazy(() => import("../pages/public/store/StorePageView"));
// Public travel storefront (Gestión de Pasajeros) — epic #10, issue #19
const RidesStorefrontLayout = lazy(() => import("../Layouts/public/RidesStorefrontLayout"));
const RidesHome = lazy(() => import("../pages/public/rides/RidesHome"));
const RidesCatalog = lazy(() => import("../pages/public/rides/RidesCatalog"));
const RideDetail = lazy(() => import("../pages/public/rides/RideDetail"));
const RidesCheckout = lazy(() => import("../pages/public/rides/RidesCheckout"));
const RidesOrderSuccess = lazy(() => import("../pages/public/rides/RidesOrderSuccess"));
const RidesPageView = lazy(() => import("../pages/public/rides/RidesPageView"));
const RidesPromociones = lazy(() => import("../pages/public/rides/RidesPromociones"));
const RidesPromocionesHub = lazy(() => import("../pages/public/rides/RidesPromocionesHub"));
const RidesTemporada = lazy(() => import("../pages/public/rides/RidesTemporada"));
const RidesPaquetesFamiliares = lazy(() => import("../pages/public/rides/RidesPaquetesFamiliares"));
const RidesMisReservas = lazy(() => import("../pages/public/rides/RidesMisReservas"));
const RidesServicioEspecial = lazy(() => import("../pages/public/rides/RidesServicioEspecial"));
const RidesHorario = lazy(() => import("../pages/public/rides/RidesHorario"));
const RidesMapaRutas = lazy(() => import("../pages/public/rides/RidesMapaRutas"));
const RidesBlog = lazy(() => import("../pages/public/rides/RidesBlog"));
const RidesBlogListado = lazy(() => import("../pages/public/rides/RidesBlogListado"));
const RidesBlogArticulo = lazy(() => import("../pages/public/rides/RidesBlogArticulo"));
const RidesContacto = lazy(() => import("../pages/public/rides/RidesContacto"));
const RidesNotificaciones = lazy(() => import("../pages/public/rides/RidesNotificaciones"));
const RidesPerfil = lazy(() => import("../pages/public/rides/RidesPerfil"));
const RidesCuenta = lazy(() => import("../pages/public/rides/RidesCuenta"));
const RidesConfiguraciones = lazy(() => import("../pages/public/rides/RidesConfiguraciones"));
const RidesAvisoPrivacidad = lazy(() => import("../pages/public/rides/RidesAvisoPrivacidad"));
const RidesTerminosUso = lazy(() => import("../pages/public/rides/RidesTerminosUso"));
import { StorefrontAccountGate } from "../pages/public/rides/storefrontFeatures";
const SystemHealthOverview = lazy(() => import("../Layouts/entrega-servicio/components/SystemHealthOverview"));
const EntregaServicioPlaceholder = lazy(() => import("../Layouts/entrega-servicio/components/EntregaServicioPlaceholder"));
import HomeView from "../pages/home/HomeView";
import DeviceLinkAuthGate from "./DeviceLinkAuthGate";
const AppsHubView = lazy(() => import("../pages/apps-hub/AppsHubView"));
import NotFound from "../pages/NotFound";
const ProfilePage = lazy(() => import("../pages/profile/ProfilePage"));
const ProfileLayout = lazy(() => import("../Layouts/profile/ProfileLayout"));

// Public Pages
const PublicTrackingLinkPage = lazy(() => import("../pages/public/tracking/PublicTrackingLinkPage"));
const PublicQuotePage = lazy(() => import("../pages/public/quote/PublicQuotePage"));
// Autofactura de tickets de estacionamiento (CC-CONSOLE #1594), sin sesión.
const ParkingSelfInvoicePage = lazy(() => import("../pages/public/parking/ParkingSelfInvoicePage"));
const CameraSessionPage = lazy(() => import("../pages/inspecciones/camera/CameraSessionPage"));
const ConfirmSessionResetPage = lazy(() => import("../pages/public/confirm-session-reset/ConfirmSessionResetPage"));
const ResetPasswordPage = lazy(() => import("../pages/public/reset-password/ResetPasswordPage"));
const RegisterView = lazy(() => import("../pages/register/RegisterView"));
const VerifySignupView = lazy(() => import("../pages/register/VerifySignupView"));
const BillingPlansView = lazy(() => import("../pages/purchase/BillingPlansView"));
const PurchaseSuccessView = lazy(() => import("../pages/purchase/PurchaseSuccessView"));
const PurchaseCancelView = lazy(() => import("../pages/purchase/PurchaseCancelView"));
const WelcomeView = lazy(() => import("../pages/onboarding/WelcomeView"));
const TrackingReplayRecordingPage = lazy(() => import("../pages/tracking-replay/TrackingReplayRecordingPage"));

// Sections Admin
const UnitsSection = lazy(() => import("../Layouts/admin/components/sections/UnitsSection"));
const DevicesSection = lazy(() => import("../Layouts/admin/components/sections/devices/DevicesSection"));
const TrailersSection = lazy(() => import("../Layouts/admin/components/sections/trailers/TrailersSection"));
const TrucksSection = lazy(() => import("../Layouts/admin/components/sections/TrucksSection"));
const GeofencesSection = lazy(() => import("../Layouts/admin/components/sections/geofences/GeofencesSection"));
const AlertsSection = lazy(() => import("../Layouts/admin/components/sections/AlertsSection"));
const NotificationsSection = lazy(() => import("../Layouts/admin/components/sections/notifications/NotificationsSection"));
const DriversSection = lazy(() => import("../Layouts/admin/components/sections/DriversSection"));
const PoliciesSection = lazy(() => import("../Layouts/admin/components/sections/PoliciesSection"));
const AvisosSection = lazy(() => import("../Layouts/admin/components/sections/avisos"));
const SessionsSection = lazy(() => import("../Layouts/admin/components/sections/sessions"));
const PointOfInterestsSection = lazy(() => import("../Layouts/admin/components/sections/PointOfInterestsSection"));
const TrackingLinksSection = lazy(() => import("../Layouts/admin/components/sections/trackingLinks/TrackingLinksSection"));
const TrackingReplaysSection = lazy(() => import("../Layouts/admin/components/sections/trackingReplays").then((m) => ({ default: m.TrackingReplaysSection })));
const GroupsSection = lazy(() => import("../Layouts/admin/components/sections/groups"));
const LicensesSection = lazy(() => import("../Layouts/reseller-admin/components/sections/licenses/LicensesSection"));
const TutorialsFlagsSection = lazy(() => import("../Layouts/reseller-admin/components/sections/tutorials/TutorialsFlagsSection"));
const ModelsSection = lazy(() => import("../Layouts/reseller-admin/components/sections/models").then((m) => ({ default: m.ModelsSection })));
const IntegratorsSection = lazy(() => import("../Layouts/reseller-admin/components/sections/integrators").then((m) => ({ default: m.IntegratorsSection })));
const ProvidersSection = lazy(() => import("../Layouts/reseller-admin/components/sections/providers").then((m) => ({ default: m.ProvidersSection })));
const UsersSection = lazy(() => import("../pages/facturacion/users/UsersSection"));
const AccessAdminSection = lazy(() => import("../pages/access/AccessAdminSection"));
const TripsSection = lazy(() => import("../Layouts/admin/components/sections/trips/TripsSection"));
const RoutesSection = lazy(() => import("../Layouts/admin/components/sections/RoutesSection"));
const DeviceOrdersSection = lazy(() => import("../Layouts/reseller-admin/components/sections/device-orders").then((m) => ({ default: m.DeviceOrdersSection })));

// Section Lite
const MapView = lazy(() => import("../pages/home/components/MapView"));


// Section SG
const ReportesRapidosSection = lazy(() => import("../pages/reports/rapidos/ReportesRapidosSection"));
const MotorInfoSection = lazy(() => import("../pages/reports/motor-info-section/MotorInfoSection"));
const HistorySection = lazy(() => import("../pages/reports/history-section/HistorySection"));
const EventsSection = lazy(() => import("../pages/reports/events-section/EventsSection"));
const SpeedSection = lazy(() => import("../pages/reports/speed-section/SpeedSection"));
const TemperatureSection = lazy(() => import("../pages/reports/temperature-section/TemperatureSection"));
const DrivingHistorySection = lazy(() => import("../pages/reports/driving-history-section/DrivingHistorySection"));
const MileageSection = lazy(() => import("../pages/reports/mileage-section/MileageSection"));
const FuelSection = lazy(() => import("../pages/reports/fuel-section/FuelSection"));
const TrailerSection = lazy(() => import("../pages/reports/trailer-section/TrailerSection"));
const GeofecensesSection = lazy(() => import("../pages/reports/geofences-section/GeofecensesSection"));
const RoutesReportSection = lazy(() => import("../pages/reports/routes-section/RoutesReportSection"));
const OdometerSection = lazy(() => import("../pages/reports/odometer-section/OdometerSection"));
const StopSection = lazy(() => import("../pages/reports/stop-section/StopSection"));
const CommandsSection = lazy(() => import("../pages/reports/commands-section/CommandsSection"));

// Sections Facturacion
const ResumenSection = lazy(() => import("../pages/facturacion/resumen/ResumenSection"));

// Sections Cobranza
const CobranzaResumenSection = lazy(() => import("../pages/cobranza/resumen/ResumenSection"));
const CompaniesSection = lazy(() => import("../pages/cobranza/companies/CompaniesSection"));

// Sections Dashboards
const CobranzaDashboard = lazy(() => import("../pages/dashboards/cobranza/CobranzaDashboard"));
const LogisticaDashboard = lazy(() => import("../pages/dashboards/logistica/LogisticaDashboard"));
const V5Dashboard = lazy(() => import("../pages/dashboards/v5/V5Dashboard"));
const PagosSection = lazy(() => import("../pages/cobranza/pagos/PagosSection"));
const AdeudosSection = lazy(() => import("../pages/cobranza/adeudos/AdeudosSection"));
const CobranzaHistorialSection = lazy(() => import("../pages/cobranza/historial/HistorialSection"));
const InformacionSection = lazy(() => import("../pages/facturacion/informacion/InformacionSection"));
const HistorialSection = lazy(() => import("../pages/facturacion/historial/HistorialSection"));
const MetodosPagoSection = lazy(() => import("../pages/facturacion/metodos-pago/MetodosPagoSection"));

const routes: RouteObject[] = [
  {
    path: "/camera/:sessionId",
    element: <CameraSessionPage />,
  },
  {
    path: "/splash",
    element: <SplashScreen />,
  },
  {
    path: "/",
    element: <HomeView />,
  },
  {
    path: "/register",
    element: <RegisterView />,
  },
  {
    // Alta manual (CC-CONSOLE#1550): confirmar el correo con el código (y,
    // opcional, el WhatsApp). Fuera del layout de apps, como /bienvenida.
    path: "/verificar",
    element: <VerifySignupView />,
  },
  {
    // Vuelta del Checkout de Stripe tras contratar un plan desde el landing.
    // No van dentro del árbol autenticado: el usuario llega de un redirect
    // externo y la pantalla se apoya en el token de localStorage, no en el
    // estado en memoria de la SPA.
    path: "/purchase/success",
    element: <PurchaseSuccessView />,
  },
  {
    // «Elige tu licencia» (#1008): último paso del alta sin plan y destino de
    // la guarda de cobro (#1009). Fuera del árbol autenticado, igual que
    // /purchase: se apoya en el token de localStorage.
    path: "/billing/plans",
    element: <BillingPlansView />,
  },
  {
    // Bienvenida guiada (#1019): a donde llega quien acaba de registrar (y
    // pagar) su empresa. Fuera del layout de apps, igual que /billing/plans.
    path: "/bienvenida",
    element: <WelcomeView />,
  },
  {
    path: "/purchase/cancel",
    element: <PurchaseCancelView />,
  },
  {
    path: "/trackingLinks/public",
    element: <PublicTrackingLinkPage />,
  },
  {
    path: "/confirm-session-reset",
    element: <ConfirmSessionResetPage />,
  },
  {
    path: "/reset-password",
    element: <ResetPasswordPage />,
  },
  {
    // Public ecommerce storefront — no auth. The company is identified by the
    // :slug tag in the URL. Mirrors the /trackingLinks/public pattern.
    path: "/shop/:slug",
    element: <StorefrontLayout />,
    children: [
      { index: true, element: <StoreHome /> },
      { path: "catalog", element: <StoreCatalog /> },
      { path: "product/:productId", element: <StoreProduct /> },
      { path: "cart", element: <StoreCart /> },
      // Cotización dentro de la tienda (CC-CONSOLE #1544).
      { path: "cotizacion", element: <StoreQuote /> },
      { path: "checkout", element: <StoreCheckout /> },
      { path: "order/success", element: <StoreOrderSuccess /> },
      // Recoger en mostrador: el QR del cliente y el estado de su pedido.
      { path: "recoger/:code", element: <StorePickup /> },
      { path: "page/:pageSlug", element: <StorePageView /> },
    ],
  },
  {
    // Public travel storefront — no auth. The company is identified by the
    // :slug tag in the URL (companies.{id}.ridesStorefront.slug). Mirrors the
    // ecommerce /shop/:slug storefront for routes/rides/seats (issue #19).
    path: "/rides/:slug",
    element: <RidesStorefrontLayout />,
    children: [
      { index: true, element: <RidesHome /> },
      { path: "catalog", element: <RidesCatalog /> },
      { path: "ride/:rideId", element: <RideDetail /> },
      { path: "checkout", element: <RidesCheckout /> },
      { path: "order/success", element: <RidesOrderSuccess /> },
      { path: "page/:pageSlug", element: <RidesPageView /> },
      { path: "promociones", element: <RidesPromocionesHub /> },
      { path: "promociones/descuentos", element: <RidesPromociones /> },
      { path: "promociones/temporada", element: <RidesTemporada /> },
      { path: "promociones/familias", element: <RidesPaquetesFamiliares /> },
      { path: "mis-reservas", element: <StorefrontAccountGate><RidesMisReservas /></StorefrontAccountGate> },
      { path: "servicio-especial", element: <RidesServicioEspecial /> },
      { path: "horario-y-paradas", element: <RidesHorario /> },
      { path: "mapa-de-rutas", element: <RidesMapaRutas /> },
      { path: "blog", element: <RidesBlog /> },
      { path: "blog/articulos", element: <RidesBlogListado /> },
      { path: "blog/:articleSlug", element: <RidesBlogArticulo /> },
      { path: "contacto", element: <RidesContacto /> },
      { path: "notificaciones", element: <StorefrontAccountGate><RidesNotificaciones /></StorefrontAccountGate> },
      { path: "perfil", element: <StorefrontAccountGate><RidesPerfil /></StorefrontAccountGate> },
      { path: "cuenta", element: <StorefrontAccountGate><RidesCuenta /></StorefrontAccountGate> },
      { path: "configuraciones", element: <StorefrontAccountGate><RidesConfiguraciones /></StorefrontAccountGate> },
      { path: "aviso-de-privacidad", element: <RidesAvisoPrivacidad /> },
      { path: "terminos-de-uso", element: <RidesTerminosUso /> },
    ],
  },
  {
    // Recoger en mostrador (ventas del POS sin tienda publicada) — sin sesión;
    // el código aleatorio del QR es la llave.
    path: "/recoger/:code",
    element: <PublicPickupPage />,
  },
  // Cotización pública (#1545) — sin sesión. El cliente la abre desde el correo
  // de la cotización (llave aleatoria de 32+ caracteres) y la acepta o rechaza.
  // Apagada donde la plataforma no opera cotizaciones del CRM (WAYAK).
  gateRouteByFeature(
    {
      path: "/cotizacion/:key",
      element: <PublicQuotePage />,
    },
    "crm-facturacion",
  ),
  {
    // Autofactura de estacionamiento (CC-CONSOLE #1594) — sin sesión: folio +
    // importe del ticket pagado (el QR del recibo trae los dos).
    path: "/autofactura-estacionamiento",
    element: <ParkingSelfInvoicePage />,
  },
  {
    // Public Service Desk client portal — no auth. Anonymous submission +
    // tokenized follow-up. Mirrors the /shop/:slug public pattern.
    path: "/soporte",
    element: <SoporteLayout />,
    children: [
      { index: true, element: <SoporteNuevo /> },
      { path: "seguimiento", element: <SoporteSeguimiento /> },
    ],
  },
  {
    // Authenticated customer "my tickets".
    path: "/mis-tickets",
    element: <RoleProtectedRoute restrictedRoles={["web_services"]} />,
    children: [{ index: true, element: <MisTicketsPage /> }],
  },
  {
    // Vincular dispositivo (CC-CONSOLE#1523): fuera del layout de apps, como
    // /bienvenida. NO va en BillingGate.OPEN_PREFIXES: escribe equipos reales.
    // Sin sesión → alta gratis con aviso y vuelta aquí (CC-CONSOLE#1561).
    path: "/dispositivos/vincular",
    element: <DeviceLinkAuthGate />,
    children: [
      {
        element: <RoleProtectedRoute restrictedRoles={["web_services"]} />,
        children: [{ index: true, element: <VincularDispositivoView /> }],
      },
    ],
  },
  {
    path: "/tracking-replay/recording",
    element: <TrackingReplayRecordingPage />,
  },
  {
    path: "/profile",
    element: <ProfileLayout />,
    children: [
      {
        index: true,
        element: <ProfilePage />,
      },
    ],
  },
  {
    path: "/apps",
    element: <RoleProtectedRoute restrictedRoles={["web_services"]} />,
    children: [
      {
        index: true,
        element: <AppsHubView />,
      },
    ],
  },
  {
    path: "/map",
    element: <LiteLayout />,
    children: [
      {
        index: true,
        element: <MapView />,
      },
    ],
  },
  {
    path: "/admin",
    element: <AdminLayout />,
    children: [
      {
        index: true,
        element: <AdminIndexRedirect />,
      },
      {
        path: "units",
        element: <UnitsSection />,
      },
      {
        path: "devices",
        element: <DevicesSection />,
      },
      {
        path: "trailers",
        element: <TrailersSection />,
      },
      {
        path: "trucks",
        element: <TrucksSection />,
      },
      {
        path: "geofences",
        element: <GeofencesSection key="geofences" />,
      },
      {
        path: "alerts",
        element: <AlertsSection />,
      },
      {
        path: "notifications",
        element: <NotificationsSection />,
      },
      {
        path: "drivers",
        element: <DriversSection />,
      },
      {
        path: "policies",
        element: <PoliciesSection />,
      },
      {
        path: "avisos",
        element: <AvisosSection />,
      },
      {
        path: "sessions",
        element: <SessionsSection />,
      },
      {
        path: "routes",
        element: <RoutesSection />,
      },
      {
        path: "pointOfInterests",
        element: <PointOfInterestsSection />,
      },
      {
        path: "tracking-links",
        element: <TrackingLinksSection />,
      },
      {
        path: "groups",
        element: <GroupsSection />,
      },
      {
        path: "tracking-replays",
        element: <TrackingReplaysSection />,
      },
      {
        path: "usuarios",
        element: <UsersSection />,
      },
      {
        path: "trips",
        element: <TripsSection />,
      },
    ],
  },
  nuvexAccessRoute,
  // Apps completas de Gestión de Staff y Finanzas internas (portadas de
  // CC-CONSOLE): son dueñas de /gestion-staff, /team, /payroll, /work-hours,
  // /expenses y /finance-settings, con los datos propios de esta plataforma.
  // Donde la plataforma las apaga (WAYAK, preset `disabledFeatures`), sus rutas
  // redirigen al inicio: no se alcanzan escribiendo la URL.
  // /gestion-staff es Actividades (familia `staff-actividades`, encendida en
  // WAYAK desde 2026-10-29); sus rutas de Horas y Equipo siguen con «staff».
  gateRouteByFeature(gateChildPathsByFeature(staffRoute, ["horas", "equipo"], "staff"), "staff-actividades"),
  gateRouteByFeature(teamRoute, "staff"),
  gateRouteByFeature(payrollRoute, "nomina"),
  gateRouteByFeature(workHoursRoute, "staff"),
  gateRouteByFeature(expensesRoute, "finanzas"),
  gateRouteByFeature(financeSettingsRoute, "finanzas"),
  // Vista del cliente desde CC-CONSOLE (CC-CONSOLE #981): Horas, Equipo, Gastos,
  // bajo /coding-cloud/... para no chocar con las apps de arriba. En CC-CONSOLE
  // (tenant coding-cloud) no aplica: ahí Coding Cloud es el proveedor, no el cliente.
  ...(import.meta.env.VITE_THEME === "coding-cloud" ? [] : clientViewRoutes),
  mantenimientoRoute,
  finanzasOperativasRoute,
  monitoreoIaRoute,
  radarCorreoRoute,
  chatIaRoute,
  cargoMarketRoute,
  basesRoute,
  amenidadesRoute,
  reservasRoute,
  rondinesRoute,
  documentacionRoute,
  // /activos lo cierra `withEntitlementGates` (App.tsx) con `app:activos`: con la
  // bandera `off` (D8) manda al Hub. Sin entrada en config/access/routes.ts: el
  // acceso lo deciden las banderas del catálogo, igual que en CC-CONSOLE.
  activosRoute,
  {
    // Public Airbnb-style amenities page — no auth. Company by :slug, mirrors
    // the public storefront (/shop/:slug).
    path: "/espacios/:slug",
    element: <PublicSpacesPage />,
  },
  serviceDeskRoute,
  // Ayuda y Tutoriales: wiki dentro de la plataforma (#1015 de CC-CONSOLE).
  // Sin entrada en config/access/routes.ts a propósito: la ayuda no se
  // bloquea por derechos de app (todo usuario con sesión la ve).
  helpRoute,
  legacyHelpRoute,
  enviosRoute,
  movikRoute,
  // /movik y /movik/* (la app de Sistemas Internos de #1535) → /b2c/movik/marca.
  ...movikLegacyRoutes,
  {
    path: "/papelera",
    element: <PapeleraLayout mode="fleet" />,
    children: [
      {
        index: true,
        element: <PapeleraIndexRedirect />,
      },
      {
        path: ":entity",
        element: <PapeleraSection />,
      },
    ],
  },
  {
    path: "/papelera-interno",
    element: <RoleProtectedRoute allowedRoleIds={TOP_ROLE_IDS} />,
    children: [
      {
        path: "",
        element: <PapeleraLayout mode="internal" />,
        children: [
          {
            index: true,
            element: <PapeleraIndexRedirect />,
          },
          {
            path: ":entity",
            element: <PapeleraSection />,
          },
        ],
      },
    ],
  },
  {
    // Oasis se retiró (Fase 3, 2026-11-02): sus patios viven en Patio de Maniobras.
    // Los enlaces viejos a /oasis/* van ahí en vez de dar 404.
    path: "/oasis/*",
    element: <Navigate to="/truck-yard" replace />,
  },
  {
    path: "/reports",
    element: <ReportLayout />,
    children: [
      // La portada. Antes esta ruta no tenía índice y quien entraba a /reports
      // se quedaba en un contenedor vacío.
      {
        index: true,
        element: <ReportesRapidosSection />,
      },
      {
        path: "motor-info",
        element: <MotorInfoSection />,
      },
      {
        path: "history",
        element: <HistorySection />,
      },
      {
        path: "events",
        element: <EventsSection />,
      },
      {
        path: "speed",
        element: <SpeedSection />,
      },
      {
        path: "temperature",
        element: <TemperatureSection />,
      },
      {
        /*
          «Conducción» se retiró: contestaba lo mismo que «Historial de
          conducción» desde otra fuente. La ruta se queda como redirección
          porque hay enlaces guardados y porque un 404 no explica nada.
        */
        path: "driving",
        element: <Navigate to="/reports/driving-history" replace />,
      },
      {
        path: "driving-history",
        element: <DrivingHistorySection />,
      },
      {
        path: "mileage",
        element: <MileageSection />,
      },
      {
        path: "fuel",
        element: <FuelSection />,
      },
      {
        path: "geofences",
        element: <GeofecensesSection />,
      },
      {
        path: "trailer",
        element: <TrailerSection />,
      },
      {
        path: "routes",
        element: <RoutesReportSection />,
      },
      {
        path: "odometer",
        element: <OdometerSection />,
      },
      {
        path: "stop",
        element: <StopSection />,
      },
      {
        path: "commands",
        element: <CommandsSection />,
      },
    ],
  },
  {
    path: "/entrega-servicio",
    element: <RoleProtectedRoute restrictedRoles={["web_services"]} />,
    children: [
      {
        path: "",
        element: <EntregaServicioLayout />,
        children: [
          {
            index: true,
            element: <EntregaServicioPlaceholder />,
          },
        ],
      },
    ],
  },
  {
    path: "/facturacion",
    element: <FacturacionLayout />,
    children: [
      {
        index: true,
        element: <ResumenSection />,
      },
      {
        path: "resumen",
        element: <ResumenSection />,
      },
      {
        path: "usuarios",
        element: <UsersSection />,
      },
      {
        path: "dispositivos",
        element: <DevicesSection />,
      },
      {
        path: "informacion",
        element: <InformacionSection />,
      },
      {
        path: "historial",
        element: <HistorialSection />,
      },
      {
        path: "metodos-pago",
        element: <MetodosPagoSection />,
      },
    ],
  },
  // Cobranza cruza adeudos con facturas del CRM e ingresos esperados de
  // Finanzas: se apaga con Finanzas internas. La facturación de licencias del
  // cliente (/facturacion) y su cobro por Stripe siguen encendidos.
  gateRouteByFeature({
    path: "/cobranza",
    element: <CobranzaOperatorRoute />,
    children: [
      {
        path: "",
        element: <CobranzaLayout />,
        children: [
          {
            index: true,
            element: <CobranzaResumenSection />,
          },
          {
            path: "resumen",
            element: <CobranzaResumenSection />,
          },
          {
            path: "companies",
            element: <CompaniesSection />,
          },
          {
            path: "usuarios",
            element: <UsersSection />,
          },
          {
            path: "dispositivos",
            element: <DevicesSection />,
          },
          {
            path: "pagos",
            element: <PagosSection />,
          },
          {
            path: "adeudos",
            element: <AdeudosSection />,
          },
          {
            path: "historial",
            element: <CobranzaHistorialSection />,
          },
        ],
      },
    ],
  }, "finanzas"),
  {
    // Configuración de Licencias — app propia de Sistemas Internos (#412).
    // Reutiliza la misma sección que ya vivía en reseller-admin → Licencias;
    // esa ruta sigue funcionando para no romper enlaces existentes.
    // Sólo plataforma (#1128): igual que /reseller-admin. El backend ya exige
    // administrador de plataforma para escribir licencias.
    path: "/licencias",
    element: <RoleProtectedRoute allowedRoleIds={TOP_ROLE_IDS} />,
    children: [
      {
        path: "",
        element: <LicenciasLayout />,
        children: [
          {
            index: true,
            element: <LicensesSection />,
          },
        ],
      },
    ],
  },
  {
    // Feature Flags (épica #1219): banderas globales de WAYAK. Sólo operadores
    // de banderas (owner, admin reseller y developer); la API exige además
    // requireFlagsOperator. El catálogo la marca `internal` vía Sistemas Internos.
    path: "/feature-flags",
    element: <FlagsOperatorRoute />,
    children: [
      {
        path: "",
        element: <FeatureFlagsLayout />,
        children: [
          {
            index: true,
            element: <FeatureFlagsSection />,
          },
        ],
      },
    ],
  },
  {
    // Analíticas (id histórico `comportamiento`, CC-CONSOLE #1397): tablero
    // general, todos los usuarios y la ficha de uso de una persona (`?u=`),
    // leídos de PostHog. Sistemas Internos, sólo plataforma: la misma guarda
    // por rol que /reseller-admin y /licencias (owner y admin reseller). La
    // API lo exige además (requirePlatformAdmin en /user-insights/*).
    path: "/comportamiento",
    element: <RoleProtectedRoute allowedRoleIds={TOP_ROLE_IDS} redirectTo="/apps" />,
    children: [
      {
        path: "",
        element: <ComportamientoLayout />,
        children: [
          { index: true, element: <AnaliticasIndex /> },
          { path: "dashboard", element: <AnaliticasDashboardPage /> },
          { path: "usuarios", element: <AnaliticasUsuariosPage /> },
        ],
      },
    ],
  },
  {
    path: "/reseller-admin",
    element: <RoleProtectedRoute allowedRoleIds={TOP_ROLE_IDS} />,
    children: [
      {
        path: "",
        element: <ResellerAdminLayout />,
        children: [
          {
            index: true,
            element: <CompaniesSection />,
          },
          {
            path: "companies",
            element: <CompaniesSection />,
          },
          {
            path: "devices",
            element: <DevicesSection />,
          },
          {
            path: "device-brands",
            element: <DevicesSection />,
          },
          {
            path: "users",
            element: <UsersSection />,
          },
          {
            path: "user-roles",
            element: <AccessAdminSection />,
          },
          {
            path: "reseller-users",
            element: <UsersSection />,
          },
          {
            path: "reseller-user-roles",
            element: <AccessAdminSection />,
          },
          {
            path: "licenses",
            element: <LicensesSection />,
          },
          {
            // Feature Flags es app propia de Sistemas Internos (/feature-flags,
            // igual que en CC-CONSOLE): la dirección de Admin Reseller, la que
            // usa CC-CONSOLE en sus enlaces viejos, lleva ahí.
            path: "feature-flags",
            element: <Navigate to="/feature-flags" replace />,
          },
          {
            // Banderas de los tutoriales in-app (#1014 de CC-CONSOLE).
            path: "tutorials",
            element: <TutorialsFlagsSection />,
          },
          {
            path: "models",
            element: <ModelsSection />,
          },
          {
            // Pedidos de dispositivos de la tienda (CC-CONSOLE#1526).
            path: "device-orders",
            element: <DeviceOrdersSection />,
          },
          {
            path: "integrators",
            element: <IntegratorsSection />,
          },
          {
            path: "providers",
            element: <ProvidersSection />,
          },
          {
            path: "avisos",
            element: <AvisosSection />,
          },
        ],
      },
      {
        path: "integrators",
        element: <IntegratorsSection />,
      },
      {
        path: "providers",
        element: <ProvidersSection />,
      },
      {
        path: "avisos",
        element: <AvisosSection />,
      },
    ],
  },
  {
    path: "/dashboards",
    // Igual que CC-CONSOLE y que /entrega-servicio: una cuenta de servicio
    // (web_services) no entra a los tableros internos.
    element: <RoleProtectedRoute restrictedRoles={["web_services"]} />,
    children: [
      {
        path: "",
        element: <DashboardsLayout />,
        children: [
          {
            index: true,
            element: <SystemHealthOverview />,
          },
          {
            path: "entrega-servicio",
            element: <SystemHealthOverview />,
          },
          {
            path: "cobranza",
            element: isPlatformFeatureEnabled("finanzas") ? <CobranzaDashboard /> : <Navigate to="/dashboards" replace />,
          },
          {
            path: "logistica",
            element: <LogisticaDashboard />,
          },
          {
            path: "v5",
            element: <V5Dashboard />,
          },
        ],
      },
    ],
  },
  {
    path: "/support",
    element: <RoleProtectedRoute restrictedRoles={["web_services"]} />,
    children: [
      {
        path: "",
        element: <SupportLayout />,
        children: [
          {
            index: true,
            element: <SupportChatPage />,
          },
        ],
      },
    ],
  },
  {
    path: "/integradores",
    element: <RoleProtectedRoute restrictedRoles={["web_services"]} />,
    children: [
      {
        path: "",
        element: <IntegradoresLayout mode="internal" />,
      },
    ],
  },
  {
    path: "/flota/integradores",
    element: <IntegradoresLayout mode="fleet" />,
  },
  {
    path: "/truck-yard",
    element: <TruckYardLayout />,
    children: [
      {
        index: true,
        element: <Navigate to="yards" replace />,
      },
      {
        path: "yards",
        element: <YardsSection />,
      },
      {
        path: "docks",
        element: <div className="p-6">Andenes - Coming soon...</div>,
      },
      {
        path: "personal",
        element: <PersonalSection />,
      },
      {
        path: "access",
        element: <AccessSection />,
      },
      {
        path: "access-control",
        element: <AccessControlSection />,
      },
      {
        path: "vehicles",
        element: <VehiclesSection />,
      },
      {
        path: "groups",
        element: <TruckYardGroupsSection />,
      },
      {
        path: "access-cards",
        element: <AccessCardsSection />,
      },
      {
        path: "trucks",
        element: <div className="p-6">Camiones - Coming soon...</div>,
      },
    ],
  },
  {
    path: "/passengers",
    element: <RoleProtectedRoute restrictedRoles={["web_services"]} />,
    children: [
      {
        path: "",
        element: <PassengersLayout />,
        children: [
          { index: true, element: <Navigate to="/passengers/home" replace /> },
          { path: "home", element: <PaxHomeSection /> },
          { path: "vehicles", element: <Navigate to="/passengers/home" replace /> },
          { path: "rides", element: <PaxRidesSection /> },
          { path: "rides/:rideId", element: <PaxRideManageSection /> },
          { path: "staff/pos", element: <PaxStaffPosSection /> },
          { path: "post-ticket", element: <PaxPostTicketSection /> },
          { path: "users", element: <PaxUsersSection /> },
          { path: "staff/scan", element: <PaxStaffScanSection /> },
          { path: "storefront", element: <PaxStorefrontSection /> },
          { path: "settings", element: <PaxSettingsSection /> },
        ],
      },
    ],
  },
  {
    path: "/respaldos",
    element: <RoleProtectedRoute allowedRoleIds={TOP_ROLE_IDS} />,
    children: [
      {
        path: "",
        element: <RespaldosLayout />,
        children: [
          {
            index: true,
            element: <RespaldosList />,
          },
          {
            path: ":jobId",
            element: <JobDetail />,
          },
        ],
      },
    ],
  },
  {
    path: "/truck-yard",
    children: [
      {
        element: <TruckYardLayout />,
        children: [
          {
            index: true,
            element: <Navigate to="yards" replace />,
          },
          {
            path: "yards",
            element: <YardsSection />,
          },
          {
            path: "docks",
            element: <div className="p-6">Andenes - Coming soon...</div>,
          },
          {
            path: "personal",
            element: <PersonalSection />,
          },
          {
            path: "access",
            element: <AccessSection />,
          },
          {
            path: "access-control",
            element: <AccessControlSection />,
          },
          {
            path: "access-records",
            element: <AccessRecordsSection />,
          },
          {
            path: "relays",
            element: <RelaySection />,
          },
          {
            path: "vehicles",
            element: <VehiclesSection />,
          },
          {
            path: "groups",
            element: <TruckYardGroupsSection />,
          },
          {
            path: "access-cards",
            element: <AccessCardsSection />,
          },
          {
            path: "trucks",
            element: <div className="p-6">Camiones - Coming soon...</div>,
          },
        ],
      },
    ],
  },
  {
    path: "/pos/cashier",
    element: <RoleProtectedRoute restrictedRoles={["web_services"]} />,
    children: [
      {
        path: "",
        element: <POSCashierLayout />,
        children: [
          {
            index: true,
            element: <TerminalSection />,
          },
          {
            path: "terminal",
            element: <TerminalSection />,
          },
          {
            path: "quick-sale",
            element: <QuickSaleSection />,
          },
          {
            path: "cart",
            element: <CartSection />,
          },
          {
            path: "checkout",
            element: <CheckoutSection />,
          },
          {
            path: "receipts",
            element: <ReceiptsSection />,
          },
          {
            path: "register",
            element: <RegisterSection />,
          },
        ],
      },
    ],
  },
  {
    path: "/pos/products",
    element: <RoleProtectedRoute restrictedRoles={["web_services"]} />,
    children: [
      {
        path: "",
        element: <POSProductsLayout />,
        children: [
          {
            index: true,
            element: <ProductListSection />,
          },
          {
            path: "list",
            element: <ProductListSection />,
          },
          {
            path: "create",
            element: <CreateProductSection />,
          },
          {
            path: "categories",
            element: <CategoriesSection />,
          },
          {
            path: "variants",
            element: <VariantsSection />,
          },
          {
            path: "pricing",
            element: <PricingSection />,
          },
          {
            path: "import",
            element: <ImportSection />,
          },
          {
            path: "export",
            element: <ExportSection />,
          },
        ],
      },
    ],
  },
  {
    path: "/pos/inventory",
    element: <RoleProtectedRoute restrictedRoles={["web_services"]} />,
    children: [
      {
        path: "",
        element: <POSInventoryLayout />,
        children: [
          {
            index: true,
            element: <StockSection />,
          },
          {
            path: "stock",
            element: <StockSection />,
          },
          {
            path: "low-stock",
            element: <LowStockSection />,
          },
          {
            path: "adjustments",
            element: <AdjustmentsSection />,
          },
          {
            path: "transfers",
            element: <TransfersSection />,
          },
          {
            path: "movements",
            element: <MovementsSection />,
          },
          {
            path: "locations",
            element: <LocationsSection />,
          },
          {
            path: "suppliers",
            element: <SuppliersSection />,
          },
        ],
      },
    ],
  },
  {
    path: "/pos/coupons",
    element: <RoleProtectedRoute restrictedRoles={["web_services"]} />,
    children: [
      {
        path: "",
        element: <POSCouponsLayout />,
        children: [
          {
            index: true,
            element: <CouponListSection />,
          },
          {
            path: "list",
            element: <CouponListSection />,
          },
          {
            path: "create",
            element: <CreateCouponSection />,
          },
          {
            path: "promotions",
            element: <PromotionsSection />,
          },
          {
            path: "discounts",
            element: <DiscountsSection />,
          },
          {
            path: "analytics",
            element: <AnalyticsSection />,
          },
          {
            path: "usage",
            element: <UsageHistorySection />,
          },
        ],
      },
    ],
  },
  {
    path: "/pos/orders",
    element: <RoleProtectedRoute restrictedRoles={["web_services"]} />,
    children: [
      {
        path: "",
        element: <POSOrdersLayout />,
        children: [
          {
            index: true,
            element: <SalesSection />,
          },
          {
            path: "sales",
            element: <SalesSection />,
          },
          {
            path: "pos-orders",
            element: <POSOrdersSection />,
          },
          {
            path: "online-orders",
            element: <OnlineOrdersSection />,
          },
          {
            // Recoger en mostrador: escanear el QR del cliente y entregar.
            path: "counter-pickup",
            element: <CounterPickupSection />,
          },
          {
            path: "refunds",
            element: <RefundsSection />,
          },
          {
            path: "returns",
            element: <ReturnsSection />,
          },
          {
            path: "receipts",
            element: <OrdersReceiptsSection />,
          },
          {
            path: "reports",
            element: <ReportsSection />,
          },
        ],
      },
    ],
  },
  {
    path: "/pos/ecommerce",
    element: <RoleProtectedRoute restrictedRoles={["web_services"]} />,
    children: [
      {
        path: "",
        element: <POSEcommerceLayout />,
        children: [
          {
            index: true,
            element: <EcommerceDashboard />,
          },
          {
            path: "dashboard",
            element: <EcommerceDashboard />,
          },
          {
            path: "storefront",
            element: <StorefrontSettings />,
          },
          {
            path: "pages",
            element: <StorePagesPage />,
          },
          {
            path: "theme",
            element: <StoreThemePage />,
          },
          {
            path: "navigation",
            element: <StoreNavigationPage />,
          },
          {
            path: "shipping",
            element: <ShippingPage />,
          },
          {
            path: "payments",
            element: <PaymentsPage />,
          },
          {
            path: "settings",
            element: <StoreSettingsPage />,
          },
        ],
      },
    ],
  },
  crmRoute,
  {
    path: "/inspecciones",
    element: <InspectionsLayout />,
    children: [
      {
        index: true,
        element: <Navigate to="/inspecciones/unidades" replace />,
      },
      {
        path: "unidades",
        element: <InspectionUnitsSectionWrapper />,
      },
      {
        path: "historial",
        element: <InspectionHistorySection />,
      },
    ],
  },
  {
    path: "/inspecciones",
    children: [
      {
        element: <InspectionsLayout />,
        children: [
          {
            index: true,
            element: <Navigate to="/inspecciones/historial" replace />,
          },
          {
            path: "historial",
            element: <InspectionHistorySection />,
          },
        ],
      },
    ],
  },
  {
    path: "*",
    element: <NotFound />,
  },
];


const withAccessGuard = (route: RouteObject): RouteObject => {
  if (typeof route.path !== "string") return route;

  const entry = findAppRouteAccess(route.path);

  if (entry === undefined) return route;

  return {
    ...route,
    element: (
      <AccessProtectedRoute
        basePath={entry.basePath}
        appId={entry.appId}
        appPrefix={entry.appPrefix}
      >
        {route.element}
      </AccessProtectedRoute>
    ),
  };
};

export default routes.map(withAccessGuard);
